import { inject } from '@angular/core';
import { InstructorService, Logger } from '@core/services';
import { InstructorEntity, InstructorsListEntity, Pagination, UserEntity } from '@core/entities';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { plainToClass, plainToInstance } from 'class-transformer';
import { DateTimeUtility, GenerateImageUrlWithToken, removeNullAndUndefinedProperties } from '@core/utils';
import { SectionType } from '@core/enums';
import { FilterQuery } from '@core/types';
import { ROLE } from '@auth';
import { RRule } from 'rrule';

const log = new Logger('UseInstructor');

export class UseInstructor {
  private readonly _instructorService = inject(InstructorService);

  getAllInstructors(pagination: Pagination, filterQuery: FilterQuery): Observable<InstructorsListEntity> {
    filterQuery = removeNullAndUndefinedProperties({
      tags: filterQuery['tags'],
      instructor_type: filterQuery['instructorType'],
    });
    return this._instructorService.find(pagination, filterQuery).pipe(
      map((res: any) => {
        return {
          pagination: res.pagination,
          instructors: res.instructor.map((instructor: InstructorEntity) => {
            const instructorEntity = plainToInstance(InstructorEntity, instructor);
            return this._transformInstructorData(instructorEntity);
          }),
        } as InstructorsListEntity;
      }),
    );
  }

  getInstructorById(id: number): Observable<InstructorEntity> {
    return this._instructorService.get(id).pipe(
      map((res: any) => {
        const instructor = plainToInstance(InstructorEntity, res);
        return this._transformInstructorData(instructor);
      }),
    );
  }

  createInstructor(formData: any): Observable<any> {
    return this._instructorService.create(formData);
  }

  updateInstructor(id: number, formData: any): Observable<any> {
    return this._instructorService.update(id, formData);
  }

  deleteInstructor(id: number): Observable<any> {
    return this._instructorService.delete(id);
  }

  getAllInternalInstructorUsers(): Observable<UserEntity[]> {
    return this._instructorService.getAllInternalInstructors().pipe(
      map((res: any) => {
        return res.internalUsers.reduce((acc: UserEntity[], instructor: UserEntity) => {
          if (instructor.role !== ROLE.GUEST) {
            const transformedInstructor = plainToClass(UserEntity, instructor);
            transformedInstructor.name = `${transformedInstructor.firstName} ${transformedInstructor.lastName}`;
            acc.push(removeNullAndUndefinedProperties(transformedInstructor));
          }
          return acc;
        }, []);
      }),
    );
  }

  private _transformInstructorData(instructor: InstructorEntity): InstructorEntity {
    const transformedInstructor = instructor;
    if (instructor.addImg) {
      transformedInstructor.imageUrl = GenerateImageUrlWithToken(instructor.id, SectionType.INSTRUCTOR);
    }
    transformedInstructor.availability = transformedInstructor.availability.map((availability) => {
      availability.timeFrom = availability.timeFrom.split(':').slice(0, 2).join(':');
      availability.timeTo = availability.timeTo.split(':').slice(0, 2).join(':');
      return availability;
    });
    transformedInstructor.availability.sort((a, b) => {
      const weekdayA = isNaN(+a.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(a.weekday) : +a.weekday;
      const weekdayB = isNaN(+b.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(b.weekday) : +b.weekday;
      return weekdayA - weekdayB;
    });

    if (transformedInstructor?.instructorBookings && transformedInstructor.instructorBookings.length > 0) {
      transformedInstructor.instructorBookings = transformedInstructor.instructorBookings.map((booking) => {
        booking.dateFrom = booking.dateFrom.split('T')[0];
        booking.startTime = booking.startTime.split(':').slice(0, 2).join(':');
        booking.endTime = booking.endTime.split(':').slice(0, 2).join(':');
        return booking;
      });

      transformedInstructor.instructorBookings.sort((a, b) => {
        const dateA = new Date(a.dateFrom);
        const dateB = new Date(b.dateFrom);
        return dateA.getTime() - dateB.getTime();
      });

      // Transform recurring instructor bookings
      transformedInstructor.instructorBookings = this._transformRecurringInstructorBookings(transformedInstructor.instructorBookings);
    }

    if (transformedInstructor?.instructorBookingDetails && transformedInstructor.instructorBookingDetails.length > 0) {
      transformedInstructor.instructorBookingDetails = transformedInstructor.instructorBookingDetails.map((booking) => {
        booking.dates = booking.dates.map((date) => {
          return {
            ...date,
            startTime: date.startTime.split(':').slice(0, 2).join(':'),
            endTime: date.endTime.split(':').slice(0, 2).join(':'),
          };
        });

        return booking;
      });
    }

    return transformedInstructor;
  }

  /**
   * Transforms instructor booking data by generating recurring booking dates based on course recurrence rules.
   * Similar to _generateAllEventDates in events composition but for instructor bookings.
   * @param instructorBookings Array of instructor bookings to transform
   * @returns Array of instructor bookings with expanded recurring dates
   */
  private _transformRecurringInstructorBookings(instructorBookings: any[]): any[] {
    const expandedBookings: any[] = [];

    instructorBookings.forEach((booking) => {
      // Check if the booking has a course with recurrence
      if (booking.course?.recurrence && !booking.course.recurrence.includes('null')) {
        try {
          const rruleSet = RRule.fromString(booking.course.recurrence);
          const recurringDates = rruleSet.all();

          recurringDates.forEach((recurringDate) => {
            const dateStr = recurringDate.toISOString().split('T')[0];

            // Create a new booking object for each recurring date
            const recurringBooking = {
              ...booking,
              dateFrom: dateStr,
              // Keep the original booking ID reference but this represents a recurring instance
              originalBookingId: booking.id,
              isRecurringInstance: true,
            };

            expandedBookings.push(recurringBooking);
          });
        } catch (error) {
          log.error('Error parsing RRule for instructor booking:', error);
          // If there's an error with RRule, keep the original booking
          expandedBookings.push(booking);
        }
      } else {
        // Non-recurring booking or no recurrence rule - keep as is
        expandedBookings.push(booking);
      }
    });

    return expandedBookings;
  }
}
